import {
    Component,
    input,
    output,
    ChangeDetectionStrategy,
    inject,
    ElementRef,
    signal,
    viewChild,
    viewChildren
} from '@angular/core';
import { ShIconComponent, ShListComponent, ShListItemComponent, ShPopoverComponent } from '@gbr-monorepo/gbr-shui';
import { LanguageCode } from '@gbr-monorepo/interfaces';
import { TranslateModule } from '@ngx-translate/core';

interface LanguageOption {
    code: LanguageCode;
    labelKey: string;
    selectedLabelKey: string;
    notSelectedLabelKey: string;
}

@Component({
    selector: 'app-language-switch',
    templateUrl: './language-switch.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TranslateModule, ShIconComponent, ShListComponent, ShListItemComponent, ShPopoverComponent],
    host: {
        '(document:click)': 'handleDocumentClick($event)'
    }
})
export class LanguageSwitchComponent {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    public readonly selectedLang = input<LanguageCode>(LanguageCode.EN);
    public readonly languageChanged = output<LanguageCode>();

    protected readonly languageMenuOpen = signal(false);
    protected readonly languageOptions: readonly LanguageOption[] = [
        {
            code: LanguageCode.EN,
            labelKey: 'general.language.english',
            selectedLabelKey: 'header.alt-texts.english-selected',
            notSelectedLabelKey: 'header.alt-texts.english-not-selected'
        },
        {
            code: LanguageCode.DE,
            labelKey: 'general.language.german',
            selectedLabelKey: 'header.alt-texts.german-selected',
            notSelectedLabelKey: 'header.alt-texts.german-not-selected'
        }
    ];

    private readonly languageTrigger = viewChild<ElementRef<HTMLButtonElement>>('languageTrigger');
    private readonly languageOptionItems = viewChildren<ShListItemComponent, ElementRef<HTMLElement>>(
        'languageOption',
        { read: ElementRef }
    );

    protected toggleLanguageMenu(event?: Event): void {
        event?.stopPropagation();

        if (this.languageMenuOpen()) {
            this.closeLanguageMenu();
            return;
        }

        this.openLanguageMenu(this.selectedLanguageIndex());
    }

    protected handleTriggerKeydown(event: KeyboardEvent): void {
        if (!['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
            return;
        }

        event.preventDefault();
        const focusIndex = event.key === 'ArrowUp' ? this.languageOptions.length - 1 : this.selectedLanguageIndex();
        this.openLanguageMenu(focusIndex);
    }

    protected handleOptionKeydown(event: KeyboardEvent, index: number): void {
        const lastIndex = this.languageOptions.length - 1;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.focusLanguageOption(index === lastIndex ? 0 : index + 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.focusLanguageOption(index === 0 ? lastIndex : index - 1);
                break;
            case 'Home':
                event.preventDefault();
                this.focusLanguageOption(0);
                break;
            case 'End':
                event.preventDefault();
                this.focusLanguageOption(lastIndex);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeLanguageMenu(true);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.changeLang(this.languageOptions[index].code);
                break;
        }
    }

    protected handleDocumentClick(event: MouseEvent): void {
        // Keep the menu open for clicks inside the switcher; only outside clicks close it.
        if (!this.languageMenuOpen() || this.elementRef.nativeElement.contains(event.target as Node)) {
            return;
        }

        this.closeLanguageMenu();
    }

    protected changeLang(language: LanguageCode): void {
        this.languageChanged.emit(language);
        this.closeLanguageMenu(true);
    }

    protected getLanguageAriaLabel(option: LanguageOption): string {
        return this.selectedLang() === option.code ? option.selectedLabelKey : option.notSelectedLabelKey;
    }

    private openLanguageMenu(focusIndex: number): void {
        this.languageMenuOpen.set(true);
        setTimeout(() => this.focusLanguageOption(focusIndex));
    }

    private closeLanguageMenu(focusTrigger = false): void {
        this.languageMenuOpen.set(false);

        if (focusTrigger) {
            setTimeout(() => this.languageTrigger()?.nativeElement.focus());
        }
    }

    private focusLanguageOption(index: number): void {
        this.languageOptionItems()[index]?.nativeElement.focus();
    }

    private selectedLanguageIndex(): number {
        const selectedIndex = this.languageOptions.findIndex(option => option.code === this.selectedLang());
        return selectedIndex === -1 ? 0 : selectedIndex;
    }
}
